import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAniList } from '../context/AniListContext';
import { useMangaSource } from '../context/MangaSourceContext';
import { useTitlePreference } from '../context/TitlePreferenceContext';
import { useUndoRedo } from '../context/UndoRedoContext';
import { useLinkSettings } from '../context/LinkSettingsContext';
import { useAlert } from '../context/ToastContext';
import { formatChapterProgress, formatVolumeProgress, getTotalChapters, getTotalVolumes, calculateMangaProgress, getMangaMediaStatusBadge } from '../utils/timeFormatter';
import { filterMangaGroupsByStatus, formatReleaseDate } from '../services/groupingService';
import { getDisplayTitle } from '../hooks/useDisplayTitle';
import { updateMangaProgress, updateMangaVolumeProgress, updateAnimeStatus, updateMediaScore } from '../services/aniListService';
import { DashboardSkeleton } from './common/SkeletonLoader';
import StatusButton from './StatusButton';
import EpisodeCounter from './EpisodeCounter';
import SiteLinksModal from './SiteLinksModal';
import ScoreInput from './ScoreInput';

import './Dashboard.css';

const STATUS_TABS = [
    { id: 'ALL', label: 'All', value: null },
    { id: 'CURRENT', label: 'Reading', value: 'CURRENT' },
    { id: 'CONTINUE', label: 'Continue', value: 'CONTINUE' },
    { id: 'PLANNING', label: 'Planning', value: 'PLANNING' },
    { id: 'PAUSED', label: 'On Hold', value: 'PAUSED' },
    { id: 'DROPPED', label: 'Dropped', value: 'DROPPED' },
    { id: 'HALF_COMPLETED', label: 'Half Completed', value: 'HALF_COMPLETED' },
    { id: 'COMPLETED', label: 'Completed', value: 'COMPLETED' }
];

const MANGA_OVERVIEW_TAB_STORAGE_KEY = 'manga_overview_last_active_tab';
const MANGA_OVERVIEW_SORT_BY_KEY = 'manga_overview_sort_by';
const MANGA_OVERVIEW_SORT_DIR_KEY = 'manga_overview_sort_direction';

const SORT_OPTIONS = [
    { id: 'name', label: '📝 Name', sortFn: (a, b, dir) => dir * a.name.localeCompare(b.name) },
    { id: 'averageScore', label: '⭐ Average Score', sortFn: (a, b, dir) => dir * (b.stats.averageScore - a.stats.averageScore) },
    { id: 'totalManga', label: '📚 Total Manga', sortFn: (a, b, dir) => dir * ((b.stats.mangaCount || b.stats.animeCount) - (a.stats.mangaCount || a.stats.animeCount)) },
    { id: 'totalChapters', label: '📖 Total Chapters', sortFn: (a, b, dir) => dir * (b.stats.totalChapters - a.stats.totalChapters) },
    { id: 'readChapters', label: '✅ Read Chapters', sortFn: (a, b, dir) => dir * (b.stats.readChapters - a.stats.readChapters) },
    { id: 'remainingChapters', label: '⏳ Remaining Chapters', sortFn: (a, b, dir) => dir * (b.stats.remainingChapters - a.stats.remainingChapters) }
];

const MangaDashboard = () => {
    const navigate = useNavigate();
    const { token } = useAniList();
    const { mangaGroups, mangaUserData, isLoading, isMangaReady, refreshData, latestChapterMap } = useMangaSource();
    const { titlePreference } = useTitlePreference();
    const { addChange } = useUndoRedo();
    const { readLinks } = useLinkSettings();
    const { showError } = useAlert();

    const [activeTab, setActiveTab] = useState(() => {
        const saved = localStorage.getItem(MANGA_OVERVIEW_TAB_STORAGE_KEY);
        const isValid = saved && STATUS_TABS.some(tab => tab.id === saved);
        return isValid ? saved : 'ALL';
    });
    const [searchQuery, setSearchQuery] = useState('');
    const [sortBy, setSortBy] = useState(() => {
        return localStorage.getItem(MANGA_OVERVIEW_SORT_BY_KEY) || 'averageScore';
    });
    const [sortDirection, setSortDirection] = useState(() => {
        return localStorage.getItem(MANGA_OVERVIEW_SORT_DIR_KEY) || 'asc';
    });
    const [selectedGroup, setSelectedGroup] = useState(null);
    const [showReadModal, setShowReadModal] = useState(false);
    const [selectedMangaForModal, setSelectedMangaForModal] = useState('');
    const [optimisticProgress, setOptimisticProgress] = useState(new Map());
    const [updatingProgress, setUpdatingProgress] = useState(new Set());
    const [optimisticVolumes, setOptimisticVolumes] = useState(new Map());
    const [updatingVolumes, setUpdatingVolumes] = useState(new Set());
    const [optimisticScores, setOptimisticScores] = useState(new Map());
    const [updatingScores, setUpdatingScores] = useState(new Set());
    const [optimisticUpdates, setOptimisticUpdates] = useState(new Map());
    const [changingStatus, setChangingStatus] = useState(new Set());

    // State for pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [loadedPages, setLoadedPages] = useState(new Set([1]));
    const ITEMS_PER_PAGE = 15;

    // Save active tab to localStorage whenever it changes
    useEffect(() => {
        localStorage.setItem(MANGA_OVERVIEW_TAB_STORAGE_KEY, activeTab);
    }, [activeTab]);

    useEffect(() => {
        localStorage.setItem(MANGA_OVERVIEW_SORT_BY_KEY, sortBy);
    }, [sortBy]);

    useEffect(() => {
        localStorage.setItem(MANGA_OVERVIEW_SORT_DIR_KEY, sortDirection);
    }, [sortDirection]);

    // Filter and sort groups
    const filteredGroups = useMemo(() => {
        let groups = filterMangaGroupsByStatus(mangaGroups, activeTab);

        // Search filter
        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase();
            const queryNoSpaces = query.replace(/\s+/g, '');
            groups = groups.filter(group => {
                const groupNameMatches = group.name.toLowerCase().includes(query) || group.name.toLowerCase().replace(/\s+/g, '').includes(queryNoSpaces);
                const entries = group.anime || group.manga || [];
                const matchingManga = entries.filter(entry => {
                    const englishTitle = entry.media.title.english || '';
                    const romanjiTitle = entry.media.title.romaji || '';
                    return englishTitle.toLowerCase().includes(query) ||
                        romanjiTitle.toLowerCase().includes(query) ||
                        englishTitle.toLowerCase().replace(/\s+/g, '').includes(queryNoSpaces) ||
                        romanjiTitle.toLowerCase().replace(/\s+/g, '').includes(queryNoSpaces);
                });
                return groupNameMatches || matchingManga.length > 0;
            });
        }

        // Apply sorting
        const sortOption = SORT_OPTIONS.find(opt => opt.id === sortBy);
        if (sortOption) {
            const direction = sortDirection === 'asc' ? 1 : -1;
            groups = [...groups].sort((a, b) => sortOption.sortFn(a, b, direction));
        }

        return groups;
    }, [mangaGroups, activeTab, searchQuery, sortBy, sortDirection, titlePreference]);

    // Calculate paginated groups to display
    const displayedGroups = useMemo(() => {
        const groupsToShow = [];
        Array.from(loadedPages).sort((a, b) => a - b).forEach(page => {
            const startIndex = (page - 1) * ITEMS_PER_PAGE;
            const endIndex = startIndex + ITEMS_PER_PAGE;
            groupsToShow.push(...filteredGroups.slice(startIndex, endIndex));
        });
        return groupsToShow;
    }, [filteredGroups, loadedPages]);

    const totalPages = Math.ceil(filteredGroups.length / ITEMS_PER_PAGE);

    // Reset to page 1 when filters change
    useEffect(() => {
        setCurrentPage(1);
        setLoadedPages(new Set([1]));
    }, [activeTab, searchQuery, sortBy, sortDirection]);

    // Buffered pagination
    useEffect(() => {
        if (currentPage === 1 && totalPages > 1 && !loadedPages.has(2)) {
            const timer = setTimeout(() => {
                setLoadedPages(prev => new Set([...prev, 2]));
                setCurrentPage(2);
            }, 100);
            return () => clearTimeout(timer);
        }
    }, [currentPage, totalPages, loadedPages]);

    const loadNextPage = React.useCallback(() => {
        const nextPage = currentPage + 1;
        if (nextPage <= totalPages && !loadedPages.has(nextPage)) {
            setLoadedPages(prev => new Set([...prev, nextPage]));
            setCurrentPage(nextPage);
        }
    }, [currentPage, totalPages, loadedPages]);

    const handleScroll = React.useCallback(() => {
        const scrollPosition = window.innerHeight + window.scrollY;
        const pageHeight = document.documentElement.scrollHeight;
        const triggerHeight = pageHeight - 1000;

        if (scrollPosition >= triggerHeight && currentPage < totalPages && !loadedPages.has(currentPage + 1)) {
            loadNextPage();
        }
    }, [currentPage, totalPages, loadedPages, loadNextPage]);

    useEffect(() => {
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [handleScroll]);

    // Calculate counts for tabs
    const tabCounts = useMemo(() => {
        const counts = {};
        STATUS_TABS.forEach(tab => {
            counts[tab.id] = filterMangaGroupsByStatus(mangaGroups, tab.value).length;
        });
        return counts;
    }, [mangaGroups, titlePreference]);

    const handleGroupClick = (groupId) => {
        const group = mangaGroups.find(g => g.id === groupId);
        if (group) {
            setSelectedGroup(group);
        }
    };

    // Handle score change
    const handleScoreChange = async (mediaId, newScoreRaw, mangaTitle) => {
        if (!token) { showError('Authentication required.', 'Auth Required'); return; }
        setOptimisticScores(prev => new Map(prev).set(mediaId, newScoreRaw));
        setUpdatingScores(prev => new Set([...prev, mediaId]));
        try {
            await updateMediaScore(mediaId, newScoreRaw, token);
            if (refreshData) refreshData(true);
        } catch (error) {
            showError(`Failed to update score for ${mangaTitle}`, 'Score Update Failed');
            setOptimisticScores(prev => { const n = new Map(prev); n.delete(mediaId); return n; });
        } finally {
            setUpdatingScores(prev => { const n = new Set(prev); n.delete(mediaId); return n; });
        }
    };

    // Handle chapter progress change
    const handleProgressChange = async (mediaId, newProgress, totalChapters, mangaTitle, mediaStatus, currentUserStatus) => {
        let clamped = Math.max(0, newProgress);
        if (totalChapters > 0) {
            clamped = Math.min(clamped, totalChapters);
        } else if (totalChapters === 0) {
            clamped = 0;
        }

        if (!token) {
            showError('Authentication required. Please reconnect with OAuth in Settings.', 'Authentication Required');
            return;
        }

        setOptimisticProgress(prev => new Map(prev).set(mediaId, clamped));
        setUpdatingProgress(prev => new Set([...prev, mediaId]));

        try {
            await updateMangaProgress(mediaId, clamped, token);
            console.log(`✅ Updated ${mangaTitle} chapter progress to ${clamped}`);
            if (refreshData) {
                refreshData(true);
            }

            // Auto-start: if progress > 0 and status is PLANNING, switch to CURRENT (Reading)
            if (clamped > 0 && currentUserStatus === 'PLANNING') {
                console.log(`▶️ Auto-starting ${mangaTitle} — progress set to ${clamped}, switching to Reading`);
                handleStatusChange(mediaId, currentUserStatus, 'CURRENT', mangaTitle);
            }

            // Auto-complete: if all chapters read and manga is finished, mark as COMPLETED
            if (clamped > 0 && totalChapters > 0 && clamped === totalChapters && mediaStatus === 'FINISHED' && currentUserStatus !== 'COMPLETED') {
                console.log(`🎉 Auto-completing ${mangaTitle} — all ${totalChapters} chapters read`);
                handleStatusChange(mediaId, currentUserStatus, 'COMPLETED', mangaTitle);
            }
        } catch (error) {
            console.error(`❌ Failed to update progress for ${mangaTitle}:`, error);
            showError(`Failed to update progress for ${mangaTitle}`, 'Update Failed');
            setOptimisticProgress(prev => {
                const next = new Map(prev);
                next.delete(mediaId);
                return next;
            });
        } finally {
            setUpdatingProgress(prev => {
                const next = new Set(prev);
                next.delete(mediaId);
                return next;
            });
        }
    };

    // Handle volume progress change
    const handleVolumeProgress = async (mediaId, newVolumes, totalVolumes, mangaTitle, mediaStatus, currentUserStatus) => {
        let clamped = Math.max(0, newVolumes);
        if (totalVolumes > 0) clamped = Math.min(clamped, totalVolumes);

        if (!token) {
            showError('Authentication required.', 'Auth Required');
            return;
        }

        setOptimisticVolumes(prev => new Map(prev).set(mediaId, clamped));
        setUpdatingVolumes(prev => new Set([...prev, mediaId]));

        try {
            await updateMangaVolumeProgress(mediaId, clamped, token);
            if (refreshData) refreshData(true);

            // Auto-start: if progress > 0 and status is PLANNING, switch to CURRENT (Reading)
            if (clamped > 0 && currentUserStatus === 'PLANNING') {
                console.log(`▶️ Auto-starting ${mangaTitle} — volume progress set to ${clamped}, switching to Reading`);
                handleStatusChange(mediaId, currentUserStatus, 'CURRENT', mangaTitle);
            }

            // Auto-complete: if all volumes read and manga is finished, mark as COMPLETED
            if (clamped > 0 && totalVolumes > 0 && clamped === totalVolumes && mediaStatus === 'FINISHED' && currentUserStatus !== 'COMPLETED') {
                console.log(`🎉 Auto-completing ${mangaTitle} — all ${totalVolumes} volumes read`);
                handleStatusChange(mediaId, currentUserStatus, 'COMPLETED', mangaTitle);
            }
        } catch (error) {
            showError(`Failed to update volume for ${mangaTitle}`, 'Update Failed');
            setOptimisticVolumes(prev => { const n = new Map(prev); n.delete(mediaId); return n; });
        } finally {
            setUpdatingVolumes(prev => { const n = new Set(prev); n.delete(mediaId); return n; });
        }
    };

    // Handle manga status change
    const handleStatusChange = async (mediaId, currentStatus, newStatus, mangaTitle) => {
        if (newStatus === currentStatus) return;
        if (!token) {
            showError('Authentication required.', 'Auth Required');
            return;
        }

        setOptimisticUpdates(prev => new Map(prev).set(mediaId, newStatus));
        setChangingStatus(prev => new Set([...prev, mediaId]));

        addChange({
            type: 'STATUS_CHANGE',
            mediaId,
            animeTitle: mangaTitle,
            oldStatus: currentStatus,
            newStatus,
            timestamp: Date.now()
        });

        try {
            await updateAnimeStatus(mediaId, newStatus, token);
            if (refreshData) refreshData(true);
            setChangingStatus(prev => { const n = new Set(prev); n.delete(mediaId); return n; });
        } catch (error) {
            setOptimisticUpdates(prev => { const n = new Map(prev); n.delete(mediaId); return n; });
            setChangingStatus(prev => { const n = new Set(prev); n.delete(mediaId); return n; });
            showError(`Failed to update status: ${error.message}`, 'Status Update Failed');
        }
    };

    // Prevent body scroll when modal is open
    useEffect(() => {
        if (selectedGroup) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
            handleScroll();
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [selectedGroup, handleScroll]);

    // Show skeleton during initial load (no groups to display yet)
    if (isLoading && (!mangaGroups || mangaGroups.length === 0)) {
        return <DashboardSkeleton />;
    }

    if (!mangaGroups || mangaGroups.length === 0) {
        return (
            <div className="overview-container">
                <div className="empty-state">
                    <div className="empty-icon">📖</div>
                    <h3>No Manga Groups Yet</h3>
                    <p>Your manga groups will appear here once loaded.</p>
                    <p>Make sure you're connected to AniList.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="overview-container">
            <div className="overview-header">
                <div className="overview-header-content">
                    <h1>📖 Manga Overview</h1>
                    <p>Your manga collection at a glance</p>
                </div>
            </div>

            {/* Search and Sort Bar */}
            <div className="search-sort-container">
                <input
                    type="text"
                    className="search-input"
                    placeholder="🔍 Search groups or manga..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                />
                <div className="sort-controls">
                    <select
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value)}
                        className="sort-select"
                        title="Sort by"
                    >
                        {SORT_OPTIONS.map(option => (
                            <option key={option.id} value={option.id}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                    <button
                        onClick={() => setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')}
                        className="sort-direction-btn"
                        title={sortDirection === 'asc' ? 'Ascending' : 'Descending'}
                    >
                        {sortDirection === 'asc' ? '↑' : '↓'}
                    </button>
                </div>
            </div>

            {/* Status Tabs */}
            <div className="status-tabs">
                {STATUS_TABS.map(tab => (
                    <button
                        key={tab.id}
                        className={`tab ${activeTab === tab.id ? 'active' : ''}`}
                        onClick={() => setActiveTab(tab.id)}
                    >
                        {tab.label}
                        <span className="tab-count">{tabCounts[tab.id] || 0}</span>
                    </button>
                ))}
            </div>

            {/* Groups Grid */}
            <div className="overview-groups-grid">
                {displayedGroups.length === 0 ? (
                    <div className="empty-state">
                        <p>
                            No groups found for {STATUS_TABS.find(tab => tab.id === activeTab)?.label || 'this filter'}{searchQuery && ` matching "${searchQuery}"`}.
                        </p>
                        {searchQuery && (
                            <p style={{ fontSize: '0.85em', opacity: 0.7, marginTop: '4px' }}>Check the spelling or try different keywords</p>
                        )}
                    </div>
                ) : (
                    displayedGroups.map(group => {
                        const oldestManga = group.anime[0];
                        const coverImage = oldestManga?.media?.coverImage?.extraLarge || oldestManga?.media?.coverImage?.large || oldestManga?.media?.coverImage?.medium;
                        const groupName = getDisplayTitle(oldestManga?.media, titlePreference);
                        const hasReleasing = group.anime.some(a => a.media?.status === 'RELEASING');

                        return (
                            <div
                                key={group.id}
                                className="overview-group-card"
                                onClick={() => handleGroupClick(group.id)}
                                role="button"
                                tabIndex={0}
                                onKeyPress={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        handleGroupClick(group.id);
                                    }
                                }}
                            >
                                {coverImage && (
                                    <img
                                        src={coverImage}
                                        alt={groupName}
                                        className="overview-cover"
                                    />
                                )}

                                <div className="overview-group-info">
                                    <h3 className="overview-group-name">{groupName}</h3>
                                    {hasReleasing && <span className="group-releasing-tag">📡 Publishing</span>}

                                    <div className="overview-stats">
                                        <span className="stat-anime-count">
                                            📚 {group.stats.mangaCount || group.stats.animeCount} manga
                                        </span>
                                        <span>•</span>
                                        <span className="stat-episodes">
                                            📖 {formatChapterProgress(group.stats.readChapters, group.stats.totalChapters)} ch
                                        </span>
                                        {group.stats.totalVolumes > 0 && (
                                            <>
                                                <span>•</span>
                                                <span className="stat-episodes">
                                                    📕 {formatVolumeProgress(group.stats.readVolumes, group.stats.totalVolumes)} vol
                                                </span>
                                            </>
                                        )}
                                        {group.stats.averageScore > 0 && (
                                            <>
                                                <span>•</span>
                                                <span className="stat-score">
                                                    ⭐ {(group.stats.averageScore / 10).toFixed(1)}
                                                </span>
                                            </>
                                        )}
                                    </div>

                                    <div className="overview-time-stats">
                                        {group.stats.remainingChapters > 0 && (
                                            <div className="time-stat remaining">
                                                <span className="time-label">⏳ Remaining</span>
                                                <span className="time-value">
                                                    {group.stats.remainingChapters} ch
                                                    {group.stats.remainingVolumes > 0 && ` / ${group.stats.remainingVolumes} vol`}
                                                </span>
                                            </div>
                                        )}
                                        {group.stats.readChapters > 0 && (
                                            <div className="time-stat watched">
                                                <span className="time-label">✅ Read</span>
                                                <span className="time-value">
                                                    {group.stats.readChapters} ch
                                                    {group.stats.readVolumes > 0 && ` / ${group.stats.readVolumes} vol`}
                                                </span>
                                            </div>
                                        )}
                                        <div className="time-stat total">
                                            <span className="time-label">📖 Total</span>
                                            <span className="time-value">
                                                {group.stats.totalChapters || '?'} ch
                                                {group.stats.totalVolumes > 0 && ` / ${group.stats.totalVolumes} vol`}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })
                )}
            </div>

            {/* Summary Footer */}
            <div className="overview-summary">
                <p>
                    Showing {filteredGroups.length} groups
                    {searchQuery && ` for "${searchQuery}"`}
                </p>
            </div>

            {/* Group Card Modal Overlay */}
            {selectedGroup && (
                <div className="group-modal-overlay" onClick={() => setSelectedGroup(null)}>
                    <div className="group-modal-content" onClick={(e) => e.stopPropagation()}>
                        <button className="close-btn" onClick={() => setSelectedGroup(null)}>×</button>

                        <div className="group-card-modal">
                            {/* Group Header */}
                            <div className="modal-group-header">
                                {selectedGroup.anime[0]?.media?.coverImage?.large && (
                                    <img
                                        src={selectedGroup.anime[0].media.coverImage.large}
                                        alt={getDisplayTitle(selectedGroup.anime[0]?.media, titlePreference)}
                                        className="modal-group-cover"
                                    />
                                )}
                                <div className="modal-group-info">
                                    <h2 className="modal-group-name">
                                        📖 {getDisplayTitle(selectedGroup.anime[0]?.media, titlePreference)}
                                    </h2>
                                    <div className="modal-group-stats">
                                        <span className="stat-anime-count">{selectedGroup.stats.mangaCount || selectedGroup.stats.animeCount} manga</span>
                                        <span>•</span>
                                        <span className="stat-episodes">
                                            {formatChapterProgress(selectedGroup.stats.readChapters, selectedGroup.stats.totalChapters)} ch
                                        </span>
                                        {selectedGroup.stats.totalVolumes > 0 && (
                                            <>
                                                <span>•</span>
                                                <span className="stat-episodes">
                                                    {formatVolumeProgress(selectedGroup.stats.readVolumes, selectedGroup.stats.totalVolumes)} vol
                                                </span>
                                            </>
                                        )}
                                        {selectedGroup.stats.remainingChapters > 0 && (
                                            <>
                                                <span>•</span>
                                                <span className="stat-remaining-time">⏳ {selectedGroup.stats.remainingChapters} ch{selectedGroup.stats.remainingVolumes > 0 ? ` / ${selectedGroup.stats.remainingVolumes} vol` : ''}</span>
                                            </>
                                        )}
                                        {selectedGroup.stats.averageScore > 0 && (
                                            <>
                                                <span>•</span>
                                                <span className="stat-score">⭐ {(selectedGroup.stats.averageScore / 10).toFixed(1)}</span>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Manga List */}
                            <div className="modal-anime-list">
                                {selectedGroup.anime.map((entry, index) => {
                                    const title = getDisplayTitle(entry.media, titlePreference);
                                    const releaseDate = formatReleaseDate(entry.media.startDate);
                                    const totalChapters = getTotalChapters(entry, latestChapterMap);
                                    const readChapters = entry.progress || 0;
                                    const totalVolumes = getTotalVolumes(entry);
                                    const readVolumes = entry.progressVolumes || 0;
                                    const mangaProgress = calculateMangaProgress(entry, latestChapterMap);

                                    return (
                                        <div key={entry.id} className="modal-anime-item">
                                            <img
                                                src={entry.media.coverImage?.extraLarge || entry.media.coverImage?.large || entry.media.coverImage?.medium}
                                                alt={title}
                                                className="anime-cover"
                                            />
                                            <div className="anime-details">
                                                <div className="anime-title-row">
                                                    <h4 className="anime-title">
                                                        <a
                                                            href={`https://anilist.co/manga/${entry.media.id}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            onClick={(e) => e.stopPropagation()}
                                                        >
                                                            {title}
                                                        </a>
                                                    </h4>
                                                    <div className="anime-action-buttons">
                                                        <ScoreInput
                                                            mediaId={entry.media.id}
                                                            currentScore={optimisticScores.has(entry.media.id) ? optimisticScores.get(entry.media.id) : (entry.score || 0)}
                                                            isUpdating={updatingScores.has(entry.media.id)}
                                                            onChange={(newScore) => handleScoreChange(entry.media.id, newScore, title)}
                                                        />
                                                        {/* Chapter Counter */}
                                                        <EpisodeCounter
                                                            mediaId={entry.media.id}
                                                            currentProgress={optimisticProgress.has(entry.media.id) ? optimisticProgress.get(entry.media.id) : readChapters}
                                                            totalEpisodes={totalChapters}
                                                            isUpdating={updatingProgress.has(entry.media.id)}
                                                            onChange={(newVal) => handleProgressChange(entry.media.id, newVal, totalChapters, title, entry.media.status, optimisticUpdates.get(entry.media.id) || entry.status)}
                                                            label="chapter"
                                                        />
                                                        {/* Volume Counter */}
                                                        {totalVolumes > 0 && (
                                                            <EpisodeCounter
                                                                mediaId={`vol-${entry.media.id}`}
                                                                currentProgress={optimisticVolumes.has(entry.media.id) ? optimisticVolumes.get(entry.media.id) : readVolumes}
                                                                totalEpisodes={totalVolumes}
                                                                isUpdating={updatingVolumes.has(entry.media.id)}
                                                                onChange={(newVal) => handleVolumeProgress(entry.media.id, newVal, totalVolumes, title, entry.media.status, optimisticUpdates.get(entry.media.id) || entry.status)}
                                                                label="volume"
                                                            />
                                                        )}
                                                        <StatusButton
                                                            currentStatus={optimisticUpdates.get(entry.media.id) || entry.status}
                                                            onChange={(newStatus) => handleStatusChange(entry.media.id, entry.status, newStatus, title)}
                                                            disabled={changingStatus.has(entry.media.id)}
                                                            isLoading={changingStatus.has(entry.media.id)}
                                                            type="manga"
                                                        />
                                                        <button
                                                            className="action-btn watch-btn"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setSelectedMangaForModal(title);
                                                                setShowReadModal(true);
                                                            }}
                                                            title="Read"
                                                        >
                                                            📖
                                                        </button>
                                                    </div>
                                                </div>
                                                <div className="anime-meta">
                                                    <span>{releaseDate}</span>
                                                    <span>{entry.media.format}</span>
                                                    <span>📖 {readChapters}/{totalChapters || '?'} ch</span>
                                                    {totalVolumes > 0 && <span>📕 {readVolumes}/{totalVolumes} vol</span>}
                                                    {mangaProgress.remainingChapters > 0 && (
                                                        <span>⏳ {mangaProgress.remainingChapters} ch{mangaProgress.remainingVolumes > 0 ? ` / ${mangaProgress.remainingVolumes} vol` : ''}</span>
                                                    )}
                                                    {entry.score > 0 && <span>⭐ {(entry.score / 10).toFixed(1)}</span>}
                                                    {entry.media.averageScore > 0 && <span>📊 {(entry.media.averageScore / 10).toFixed(1)}</span>}
                                                    {(() => { const badge = getMangaMediaStatusBadge(entry.media?.status); return <span className={`media-status-badge ${badge.className}`}>{badge.emoji} {badge.label}</span>; })()}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Site Links Modals */}
            <SiteLinksModal
                isOpen={showReadModal}
                onClose={() => {
                    setShowReadModal(false);
                    setSelectedMangaForModal('');
                }}
                links={readLinks}
                animeName={selectedMangaForModal || ''}
                type="read"
            />
        </div>
    );
};

export default MangaDashboard;
